import {
  AutocompleteItem,
  MediaTypesEnum,
  getIsValidURLFormat,
} from '@prismamedia/one-components';
import {
  ArticleFormat,
  ArticleStatus,
  BrandKey,
} from '../__generated__/queries-web';
import { NO_SOURCE_ID } from '../pages/shared/ListPage/getListPageVariables';

export interface AdvancedSearchParams {
  search?: string;
  categories?: AutocompleteItem[];
  tags?: AutocompleteItem[];
  source?: string;
  qualifier?: string;
  creator?: AutocompleteItem;
  author?: AutocompleteItem;
  updatedAfter?: Date;
  updatedBefore?: Date;
  imageSource?: AutocompleteItem;
  credit?: string;
  containPush?: boolean;
  containLive?: boolean;
  status_in?: ArticleStatus[];
  containVideo?: boolean;
  containDiapo?: boolean;
  containPodcast?: boolean;
}

export const formatArticleSearchBarInput = (
  articleFormat: ArticleFormat,
  searchParams: AdvancedSearchParams,
  status: ArticleStatus = ArticleStatus.Published,
  brandKey?: BrandKey,
) => {
  let editedAfterDate: Date | undefined;
  if (searchParams.updatedAfter) {
    editedAfterDate = new Date(searchParams.updatedAfter);
    editedAfterDate?.setHours(0);
    editedAfterDate?.setMinutes(0);
    editedAfterDate?.setSeconds(0);
  }

  let editedBeforeDate: Date | undefined;
  if (searchParams.updatedBefore) {
    editedBeforeDate = new Date(searchParams.updatedBefore);
    editedBeforeDate?.setHours(23);
    editedBeforeDate?.setMinutes(59);
    editedBeforeDate?.setSeconds(59);
  }

  const andConditions = [
    ...(searchParams.status_in ? [{ status_in: searchParams.status_in }] : []),
    ...(searchParams.imageSource?.label &&
    typeof searchParams.imageSource.label === 'string'
      ? [{ medias_source: searchParams.imageSource.label }]
      : []),
    ...(searchParams.credit ? [{ medias_credit: searchParams.credit }] : []),
    ...(searchParams.containDiapo
      ? [{ medias_site: MediaTypesEnum.SLIDESHOW_TYPE }]
      : []),
    ...(searchParams.containPodcast
      ? [{ medias_site: MediaTypesEnum.PODCAST_TYPE }]
      : []),
  ];

  const orConditions = searchParams.containVideo
    ? [
        { medias_site: MediaTypesEnum.BRIGHTCOVE_TYPE },
        { medias_site: MediaTypesEnum.DAILYMOTION_TYPE },
        { medias_site: MediaTypesEnum.YOUTUBE_TYPE },
      ]
    : [];

  const statusAsArray =
    status === ArticleStatus.Scheduled
      ? [ArticleStatus.Scheduled, ArticleStatus.ScheduledForRepublication]
      : [status];

  return {
    ...(searchParams.search &&
      !getIsValidURLFormat(searchParams.search) && {
        title: searchParams.search,
      }),
    format: articleFormat,
    status_in: searchParams.status_in ? undefined : statusAsArray,
    brandKey,
    creatorId: searchParams.creator?.id,
    authors_some: searchParams.author
      ? { author: { id: searchParams.author.id } }
      : undefined,
    editedAt_gte: editedAfterDate?.toISOString(),
    editedAt_lte: editedBeforeDate?.toISOString(),
    live: searchParams.containLive
      ? { posts_some: { publishedAt_gt: new Date(0).toISOString() } }
      : undefined,
    pushes_some: searchParams.containPush
      ? { sentAt_gte: new Date(0).toISOString() }
      : undefined,
    qualifiers_some: searchParams.qualifier
      ? { qualifier: { id: searchParams.qualifier } }
      : undefined,
    source:
      searchParams.source && searchParams.source !== NO_SOURCE_ID
        ? { id: searchParams.source }
        : undefined,
    source_is_null: searchParams?.source === NO_SOURCE_ID || undefined,
    [articleFormat === ArticleFormat.Video
      ? 'channels_some'
      : 'categories_some']: searchParams.categories
      ? {
          category: { id_in: searchParams.categories.map(({ id }) => id) },
        }
      : undefined,
    tags_some: searchParams.tags
      ? { tag: { id_in: searchParams.tags.map(({ id }) => id) } }
      : undefined,
    ...(andConditions.length && { AND: andConditions }),
    ...(orConditions.length && { OR: orConditions }),
  };
};
